import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";

import FormProvider, { RHFTextField } from "../../../components/hook-form";
import { TableHeadCustom, TableNoData } from "../../../components/table";
import OrderSummary from "./OrderSummary";
import moment from "moment";
import { TillService } from "../../../api/till.service";
import { capitalize } from "lodash";

// Updated interface for API response
interface TillApiResponse {
  till: {
    id: number;
    wallet: string;
    dispenseTills: [
      {
        amount: number;
        status: string;
        dispenseId: number;
        paymentDate: any;
        paymentMethod: any;
      },
    ];
  };
  patient: {
    id: number;
    firstName: string;
    surName: string;
    lastName: string;
    phone: string;
    email: string;
  };
}

const TABLE_HEAD = [
  { id: "dispenseId", label: "Dispense ID", align: "center" },
  { id: "amount", label: "Amount", align: "center" },
  { id: "status", label: "Status", align: "center" },
  { id: "paymentDate", label: "Payment Date", align: "center" },
  { id: "paymentMethod", label: "Payment Method", align: "center" },
  { id: "action", label: "Action", align: "center" },
];

export default function TillPage() {
  const [cashExpanded, setCashExpanded] = useState(false);
  const [cardExpanded, setCardExpanded] = useState(false);
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState<string>("");
  const [voucherAmount, setVoucherAmount] = useState<string>("");

  const [open, setOpen] = useState(false);
  const [tillApiData, setTillApiData] = useState<TillApiResponse | null>(null);
  const [selectedDispenseTill, setSelectedDispenseTill] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const location = useLocation();
  const { patientData } = location.state || {};

  // Always view mode
  const isViewMode = true;

  const methods = useForm({
    defaultValues: {
      name: patientData?.name || "",
      phone: patientData?.phone || "",
      email: patientData?.email || "",
      date: "",
      time: "",
      wallet: "",
    },
  });

  const { reset } = methods;

  const { tillId } = useParams();

  // Updated getTill function to handle the API response
  const getTill = async () => {
    try {
      setLoading(true);
      if (tillId) {
        const tillService = new TillService();
        const response = await tillService.getTillData(Number(tillId));
        setTillApiData(response.data);

        // Auto-select the first dispenseTill if available
        if (response.data?.till?.dispenseTills?.length > 0) {
          setSelectedDispenseTill(response.data.till.dispenseTills[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching till data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle selecting a dispenseTill
  const handleSelectDispenseTill = (dispenseTill: any) => {
    setSelectedDispenseTill(dispenseTill);
  };

  // Calculate outstanding balance
  const getOutstandingBalance = () => {
    if (!selectedDispenseTill) return 0;
    const payments = selectedDispenseTill.payments || [];
    const totalPaid = payments.reduce(
      (sum: number, p: any) => sum + p.amount,
      0,
    );
    const amountPaid =
      totalPaid > 0
        ? totalPaid
        : selectedDispenseTill.status === "paid"
          ? selectedDispenseTill.amount
          : 0;
    return selectedDispenseTill.amount - amountPaid;
  };

  const handleRecordPayment = async (method: string, amount: string) => {
    if (
      !amount ||
      isNaN(Number(amount)) ||
      !tillApiData ||
      !selectedDispenseTill
    )
      return;
    try {
      setLoading(true);
      const tillService = new TillService();
      await tillService.recordPayment(
        tillApiData.patient.id,
        selectedDispenseTill.dispenseId,
        {
          amount: Number(amount),
          paymentMethod: method,
        },
      );
      // Refresh data
      await getTill();
      // Reset states
      setCashExpanded(false);
      setCardExpanded(false);
      setVoucherOpen(false);
      setPaymentAmount("");
      setVoucherAmount("");
    } catch (error) {
      console.error("Error recording payment:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle Stripe payment for selected dispenseTill
  const handleStripePayment = async () => {
    if (!tillApiData?.till?.id || !selectedDispenseTill) {
      console.error("Till ID or selected dispense till not available");
      return;
    }

    try {
      setStripeLoading(true);
      const tillService = new TillService();
      const response = await tillService.payWithStripe(
        tillApiData.patient.id,
        selectedDispenseTill.dispenseId,
      );

      // The Stripe API will handle the redirect to Stripe checkout
      if (response.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error processing Stripe payment:", error);
      // Handle error (show toast notification, etc.)
    } finally {
      setStripeLoading(false);
    }
  };

  // Update form when user data, till data, or API data becomes available
  useEffect(() => {
    if (tillApiData && selectedDispenseTill) {
      const apiPatient = tillApiData.patient;

      reset({
        name: [apiPatient.firstName, apiPatient.surName]
          .filter(Boolean)
          .join(" "),
        phone: apiPatient.phone || "",
        email: apiPatient.email || "",
        date: selectedDispenseTill?.paymentDate || "",
        time: "",
        wallet: `£${tillApiData.till.wallet}`,
      });
    }
  }, [tillApiData, selectedDispenseTill, reset]);

  // Call getTill on component mount
  useEffect(() => {
    getTill();
  }, []);

  const typographyStyles = {
    display: "flex",
    justifyContent: "space-between",
    fontWeight: 100,
    color: "black",
  };

  const buttonStyles = {
    fontWeight: "300",
    fontSize: "12px",
    px: 1,
  };

  return (
    <FormProvider methods={methods}>
      {loading && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography>Loading till data...</Typography>
        </Box>
      )}

      {/* Dispense Tills Table */}
      {tillApiData && (
        <Box sx={{ p: 2, display: "flex", width: "100%", mb: 4 }}>
          <Box
            sx={{
              borderRadius: "15px",
              boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.16)",
              maxHeight: "400px",
              width: "100%",
              overflowY: "auto",
              overflowX: "auto",
            }}
          >
            <Table sx={{ minWidth: "auto" }}>
              <TableHeadCustom headLabel={TABLE_HEAD} />
              <TableBody>
                {tillApiData.till.dispenseTills.map((dispenseTill, index) => (
                  <TableRow
                    key={index}
                    sx={(theme) => ({
                      backgroundColor:
                        selectedDispenseTill?.dispenseId ===
                        dispenseTill.dispenseId
                          ? theme.palette.action.selected
                          : "transparent",
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    })}
                  >
                    <TableCell align="center">
                      #{dispenseTill.dispenseId}
                    </TableCell>
                    <TableCell align="center">£{dispenseTill.amount}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={capitalize(dispenseTill.status)}
                        color={
                          dispenseTill.status === "pending"
                            ? "warning"
                            : "success"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {dispenseTill.paymentDate
                        ? moment(dispenseTill.paymentDate).format("DD/MM/YYYY")
                        : "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      {dispenseTill.paymentMethod || "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant={
                          selectedDispenseTill?.dispenseId ===
                          dispenseTill.dispenseId
                            ? "contained"
                            : "outlined"
                        }
                        size="small"
                        onClick={() => handleSelectDispenseTill(dispenseTill)}
                        sx={{ fontWeight: "300", fontSize: "12px" }}
                      >
                        {selectedDispenseTill?.dispenseId ===
                        dispenseTill.dispenseId
                          ? "Selected"
                          : "Select"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                <TableNoData
                  isNotFound={tillApiData.till.dispenseTills.length <= 0}
                />
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}

      {/* Info and Wallet Section */}
      <Box
        sx={{
          px: 1,
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box sx={{ width: "65%" }}>
          <Typography variant="h6" sx={{ ml: "2px" }}>
            Patient Info
          </Typography>
          <RHFTextField name="name" disabled={isViewMode} />
        </Box>
        <Box sx={{ width: "35%" }}>
          <Typography variant="h6" sx={{ ml: "2px" }}>
            Wallet Balance
          </Typography>
          <RHFTextField name="wallet" disabled={isViewMode} />
        </Box>
      </Box>

      {/* Main Content Section */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "start",
          gap: 1,
          mt: 4,
        }}
      >
        {/* Left Section */}
        <Box sx={{ px: 1, width: "65%" }}>
          {/* Date & Time */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box sx={{ width: "50%" }}>
              <Typography variant="h6" sx={{ ml: 1 }}>
                Payment Date
              </Typography>
              <RHFTextField
                name="date"
                value={
                  selectedDispenseTill?.paymentDate
                    ? moment(selectedDispenseTill.paymentDate).format(
                        "YYYY-MM-DD",
                      )
                    : "-"
                }
                disabled={isViewMode}
              />
            </Box>
            <Box sx={{ width: "50%" }}>
              <Typography variant="h6" sx={{ ml: 1 }}>
                Time
              </Typography>
              <RHFTextField
                name="time"
                value={
                  selectedDispenseTill?.paymentDate
                    ? moment(selectedDispenseTill.paymentDate).format("HH:mm")
                    : "-"
                }
                disabled={isViewMode}
              />
            </Box>
          </Box>

          {/* Phone Number & Email */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 4,
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: "50%",
                border: "1px solid #ccc",
                padding: "16px 6px",
              }}
            >
              <Typography variant="h6" sx={{ ml: "2px", mb: 2 }}>
                Phone Number
              </Typography>
              <RHFTextField
                name="phone"
                fullWidth
                disabled={isViewMode}
                sx={{
                  "& .MuiInputBase-input": {
                    paddingTop: "16px",
                    paddingBottom: "16px",
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                width: "50%",
                border: "1px solid #ccc",
                padding: "16px 6px",
              }}
            >
              <Typography variant="h6" sx={{ ml: "2px", mb: 2 }}>
                E-mail
              </Typography>
              <RHFTextField
                name="email"
                fullWidth
                disabled={isViewMode}
                sx={{
                  "& .MuiInputBase-input": {
                    paddingTop: "16px",
                    paddingBottom: "16px",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Payment Methods Section - Only show if dispense till is pending */}
          {selectedDispenseTill &&
            selectedDispenseTill.status === "pending" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Typography variant="h6">Payment Methods</Typography>
                  <Button
                    variant="outlined"
                    sx={{ fontWeight: "300" }}
                    onClick={() => setOpen(true)}
                    disabled={
                      parseFloat(tillApiData?.till?.wallet || "0") <
                      selectedDispenseTill.amount
                    }
                  >
                    Pay From Wallet (£{tillApiData?.till?.wallet || "0"})
                  </Button>
                </Box>

                {/* Pay From Wallet Dialog */}
                <Dialog
                  open={open}
                  onClose={() => setOpen(false)}
                  sx={{ "& .MuiPaper-root": { borderRadius: "0 !important" } }}
                >
                  <DialogContent sx={{ p: 4, mt: 2 }}>
                    <Typography>
                      Confirm: You want to use balance from Wallet (£
                      {tillApiData?.till?.wallet || "0"}).
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Current wallet balance: £
                      {tillApiData?.till?.wallet || "0"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Amount to pay: £{selectedDispenseTill?.amount || "0"}
                    </Typography>
                    {parseFloat(tillApiData?.till?.wallet || "0") <
                      selectedDispenseTill?.amount && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        Insufficient wallet balance!
                      </Typography>
                    )}
                  </DialogContent>
                  <DialogActions
                    sx={{
                      gap: 1,
                      justifyContent: "flex-end",
                      pt: "0 !important",
                    }}
                  >
                    <Button
                      onClick={() => setOpen(false)}
                      color="success"
                      variant="outlined"
                      size="small"
                      disabled={
                        parseFloat(tillApiData?.till?.wallet || "0") <
                        selectedDispenseTill?.amount
                      }
                      sx={{ minWidth: "50px", padding: "2px 8px" }}
                    >
                      Yes
                    </Button>
                    <Button
                      onClick={() => setOpen(false)}
                      color="error"
                      variant="outlined"
                      size="small"
                      sx={{
                        minWidth: "50px",
                        padding: "2px 8px",
                        ml: "0 !important",
                      }}
                    >
                      No
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Payment Tenders */}
                <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant={cashExpanded ? "contained" : "outlined"}
                      fullWidth
                      onClick={() => {
                        setCashExpanded(!cashExpanded);
                        setCardExpanded(false);
                        setPaymentAmount(getOutstandingBalance().toString());
                      }}
                      sx={{ height: "56px" }}
                    >
                      Cash
                    </Button>
                    <Button
                      variant={cardExpanded ? "contained" : "outlined"}
                      fullWidth
                      onClick={() => {
                        setCardExpanded(!cardExpanded);
                        setCashExpanded(false);
                        setPaymentAmount(getOutstandingBalance().toString());
                      }}
                      sx={{ height: "56px" }}
                    >
                      Card (PDQ / Stripe)
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        setVoucherOpen(true);
                        setVoucherAmount(getOutstandingBalance().toString());
                      }}
                      sx={{ height: "56px" }}
                    >
                      NHS Voucher
                    </Button>
                  </Box>

                  {/* Cash Section */}
                  <Collapse in={cashExpanded}>
                    <Box
                      sx={{
                        p: 2,
                        border: "1px solid #ccc",
                        borderRadius: 1,
                        mt: 1,
                      }}
                    >
                      <Typography variant="h6" gutterBottom>
                        Cash Payment
                      </Typography>
                      <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        InputProps={{ startAdornment: "£" }}
                      />
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{ mt: 2 }}
                        onClick={() =>
                          handleRecordPayment("cash", paymentAmount)
                        }
                        disabled={loading}
                      >
                        Record Cash Payment
                      </Button>
                    </Box>
                  </Collapse>

                  {/* Card Section */}
                  <Collapse in={cardExpanded}>
                    <Box
                      sx={{
                        p: 2,
                        border: "1px solid #ccc",
                        borderRadius: 1,
                        mt: 1,
                      }}
                    >
                      <Typography variant="h6" gutterBottom>
                        Card Payment
                      </Typography>

                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleStripePayment}
                        disabled={stripeLoading}
                        sx={{ mb: 2 }}
                      >
                        {stripeLoading
                          ? "Processing..."
                          : "Pay with Stripe (Online)"}
                      </Button>

                      <Typography
                        variant="body2"
                        sx={{ my: 2, textAlign: "center" }}
                      >
                        - OR -
                      </Typography>

                      <Typography variant="subtitle2" gutterBottom>
                        Record External Terminal (PDQ)
                      </Typography>
                      <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        InputProps={{ startAdornment: "£" }}
                      />
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{ mt: 2 }}
                        onClick={() =>
                          handleRecordPayment("card", paymentAmount)
                        }
                        disabled={loading}
                      >
                        Record PDQ Payment
                      </Button>
                    </Box>
                  </Collapse>
                </Box>

                {/* NHS Voucher Dialog */}
                <Dialog
                  open={voucherOpen}
                  onClose={() => setVoucherOpen(false)}
                >
                  <DialogContent sx={{ p: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      NHS Voucher
                    </Typography>
                    <TextField
                      fullWidth
                      label="Voucher Value"
                      type="number"
                      value={voucherAmount}
                      onChange={(e) => setVoucherAmount(e.target.value)}
                      InputProps={{ startAdornment: "£" }}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setVoucherOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() =>
                        handleRecordPayment("voucher", voucherAmount)
                      }
                      disabled={loading}
                    >
                      Apply Voucher
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            )}

          {/* Show payment status if already paid */}
          {selectedDispenseTill &&
            selectedDispenseTill.status !== "pending" && (
              <Box
                sx={(theme) => ({
                  p: 2,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.success.main}`,
                  mt: 2,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? theme.palette.background.paper // dark mode me neutral/dark box
                      : "#e8f5e8", // light mode me green
                })}
              >
                <Typography variant="h6" color="success.main">
                  Payment Completed
                </Typography>
                <Typography variant="body2">
                  This dispense till has already been paid using{" "}
                  {selectedDispenseTill.paymentMethod || "Unknown method"}
                </Typography>
                {selectedDispenseTill.paymentDate && (
                  <Typography variant="body2">
                    Payment Date:{" "}
                    {moment(selectedDispenseTill.paymentDate).format(
                      "DD/MM/YYYY HH:mm",
                    )}
                  </Typography>
                )}
              </Box>
            )}

          {/* No Selection Message */}
          {!selectedDispenseTill && tillApiData && (
            <Box
              sx={{
                p: 2,
                bgcolor: "#fff3e0",
                borderRadius: 1,
                border: "1px solid #ff9800",
                mt: 2,
              }}
            >
              <Typography variant="h6" color="warning.main">
                No Dispense Till Selected
              </Typography>
              <Typography variant="body2">
                Please select a dispense till from the table above to view and
                manage payment details.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right Section (Order Summary) */}
        <OrderSummary
          typographyStyles={typographyStyles}
          buttonStyles={buttonStyles}
          tillApiData={tillApiData}
          selectedDispenseTill={selectedDispenseTill} // Pass selected dispenseTill
        />
      </Box>
    </FormProvider>
  );
}
